import type { RouteRecordRaw } from 'vue-router';

import {
  FeatureAccess,
  redirectBaseDisputesToReasonCodes,
  requireAdminRedirect,
  requiresFeatureAccess,
} from './utils';

export const playbookv1Route: RouteRecordRaw = {
  beforeEnter: [
    requireAdminRedirect('playbook.templates.email'),
    requiresFeatureAccess(FeatureAccess.Playbook, 'home'),
  ],
  children: [
    {
      children: [
        {
          children: [
            {
              component: () => import('@/pages/playbook/collection/templates/:id.vue'),
              meta: { label: 'Template' },
              name: 'playbook.collection.templates.id',
              path: ':id',
            },
          ],
          component: () => import('@/pages/playbook/collection/templates.vue'),
          meta: { icon: 'fa-books', label: 'Email Templates' },
          name: 'playbook.collection.templates',
          path: 'templates',
        },
        {
          children: [
            {
              component: () => import('@/pages/playbook/collection/sms-templates/:id.vue'),
              meta: { label: 'SMS Template' },
              name: 'playbook.collection.sms-templates.id',
              path: ':id',
            },
          ],
          component: () => import('@/pages/playbook/collection/sms-templates.vue'),
          meta: { icon: 'fa-message-sms', label: 'SMS Templates' },
          name: 'playbook.collection.sms-templates',
          path: 'sms-templates',
        },
        {
          component: () => import('@/pages/playbook/collection/segmentation.vue'),
          meta: {
            icon: 'fa-split',
            label: 'Customer Segmentation',
          },
          name: 'playbook.collection.segmentation',
          path: 'segmentation',
        },
        {
          component: () => import('@/pages/playbook/collection/call-guidelines.vue'),
          meta: {
            icon: 'fa-phone',
            label: 'Call Guidelines',
          },
          name: 'playbook.collection.call-guidelines',
          path: 'call-guidelines',
        },
        {
          component: () => import('@/pages/playbook/collection/dunning-states.vue'),
          meta: { icon: 'fa-layer-group', label: 'Dunning States' },
          name: 'playbook.collection.dunning-states',
          path: 'dunning-states',
        },
        {
          component: () => import('@/pages/playbook/collection/settings.vue'),
          meta: { icon: 'fa-gear', label: 'Settings' },
          name: 'playbook.collection.settings',
          path: 'settings',
        },
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.collection.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.collection.knowledge.article',
            backRoute: 'playbook.collection.needs-review',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.collection.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
        {
          component: () => import('@/pages/playbook/shared/needs-review.vue'),
          meta: { label: 'Needs Review', icon: 'fa-microscope', collection: 'collections' },
          name: 'playbook.collection.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/shared/proposal.vue'),
          meta: { label: 'Proposal' },
          name: 'playbook.collection.proposal',
          path: 'proposal/:proposalId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: {
        description: 'Define how your agent monitors and follows up on open invoices.',
        icon: 'fa-album-collection-circle-plus',
        label: 'Collections',
      },
      name: 'playbook.collection',
      path: 'collection',
      props: { domain: 'collections' },
      redirect: {
        name: 'playbook.collection.needs-review',
      },
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/cash-app/guidelines.vue'),
          meta: { icon: 'fa-sliders-simple', label: 'Guidelines' },
          name: 'playbook.cash-app.guidelines',
          path: 'guidelines',
        },
        {
          component: () => import('@/pages/playbook/cash-app/permissions.vue'),
          meta: { icon: 'fa-lock-keyhole-open', label: 'Permissions' },
          name: 'playbook.cash-app.permissions',
          path: 'permissions',
        },
        {
          component: () => import('@/pages/playbook/cash-app/adjustments.vue'),
          meta: { icon: 'fa-coins', label: 'Adjustments' },
          name: 'playbook.cash-app.adjustments',
          path: 'adjustments',
        },
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.cash-app.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.cash-app.knowledge.article',
            backRoute: 'playbook.cash-app.needs-review',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.cash-app.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
        {
          component: () => import('@/pages/playbook/shared/needs-review.vue'),
          meta: { icon: 'fa-microscope', label: 'Needs Review', collection: 'cash_application' },
          name: 'playbook.cash-app.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/shared/proposal.vue'),
          meta: { label: 'Proposal' },
          name: 'playbook.cash-app.proposal',
          path: 'proposal/:proposalId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: {
        description: 'Configure how your agent applies and reconciles payments.',
        icon: 'fa-money-bill-transfer',
        label: 'Cash App',
      },
      name: 'playbook.cash-app',
      path: 'cash-app',
      props: { domain: 'cash_application' },
      redirect: {
        name: 'playbook.cash-app.needs-review',
      },
    },
    {
      beforeEnter: requiresFeatureAccess(FeatureAccess.BaseDisputes, 'playbook.home'),
      children: [
        {
          beforeEnter: redirectBaseDisputesToReasonCodes(),
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.disputes.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.disputes.knowledge.article',
            backRoute: 'playbook.disputes.needs-review',
          },
        },
        {
          beforeEnter: redirectBaseDisputesToReasonCodes(),
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.disputes.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
        {
          beforeEnter: redirectBaseDisputesToReasonCodes(),
          component: () => import('@/pages/playbook/shared/needs-review.vue'),
          meta: { icon: 'fa-microscope', label: 'Needs Review', collection: 'disputes' },
          name: 'playbook.disputes.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/disputes/codes.vue'),
          meta: { icon: 'fa-tags', label: 'Reason Codes' },
          name: 'playbook.disputes.codes',
          path: 'codes',
        },
        {
          beforeEnter: redirectBaseDisputesToReasonCodes(),
          component: () => import('@/pages/playbook/disputes/outreach-pause.vue'),
          meta: { icon: 'fa-pause', label: 'Pause Outreach Scope' },
          name: 'playbook.disputes.outreach-pause',
          path: 'outreach-pause',
        },
        {
          beforeEnter: redirectBaseDisputesToReasonCodes(),
          component: () => import('@/pages/playbook/shared/proposal.vue'),
          meta: { label: 'Proposal' },
          name: 'playbook.disputes.proposal',
          path: 'proposal/:proposalId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: {
        description: 'Configure dispute reason codes for your workflow',
        icon: 'fa-balance-scale',
        label: 'Disputes',
      },
      name: 'playbook.disputes',
      path: 'disputes',
      props: { domain: 'disputes' },
      redirect: {
        name: 'playbook.disputes.needs-review',
      },
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/deductions/config.vue'),
          meta: { icon: 'fa-book-open', label: 'Agent Knowledge' },
          name: 'playbook.deductions.agent-knowledge',
          path: 'agent-knowledge',
        },
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.deductions.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.deductions.knowledge.article',
            backRoute: 'playbook.deductions.agent-knowledge',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.deductions.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: { icon: 'fa-percentage', label: 'Deductions' },
      name: 'playbook.deductions',
      path: 'deductions',
      props: { domain: 'deductions' },
      redirect: { name: 'playbook.deductions.agent-knowledge' },
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.credit.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.credit.knowledge.article',
            backRoute: 'playbook.credit.needs-review',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.credit.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
        {
          component: () => import('@/pages/playbook/shared/needs-review.vue'),
          meta: { icon: 'fa-microscope', label: 'Needs Review', collection: 'credit_risk' },
          name: 'playbook.credit.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/shared/proposal.vue'),
          meta: { label: 'Proposal' },
          name: 'playbook.credit.proposal',
          path: 'proposal/:proposalId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: { label: 'Credit' },
      name: 'playbook.credit',
      path: 'credit',
      props: { domain: 'credit_risk' },
      redirect: {
        name: 'playbook.credit.needs-review',
      },
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/company/w9-form.vue'),
          meta: { icon: 'fa-layer-group', label: 'W9 Form' },
          name: 'playbook.company.w9-form',
          path: 'w9-form',
        },
        {
          component: () => import('@/pages/playbook/company/ach-document.vue'),
          meta: { icon: 'fa-coins', label: 'ACH Document' },
          name: 'playbook.company.ach-document',
          path: 'ach-document',
        },
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.company.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.company.knowledge.article',
            backRoute: 'playbook.company.needs-review',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.company.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
        {
          component: () => import('@/pages/playbook/shared/needs-review.vue'),
          meta: { icon: 'fa-microscope', label: 'Needs Review', collection: 'company' },
          name: 'playbook.company.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/shared/proposal.vue'),
          meta: { label: 'Proposal' },
          name: 'playbook.company.proposal',
          path: 'proposal/:proposalId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: {
        icon: 'fa-buildings',
        label: 'Company',
      },
      name: 'playbook.company',
      path: 'company',
      props: { domain: 'company' },
      redirect: {
        name: 'playbook.company.needs-review',
      },
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.payments.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.payments.knowledge.article',
            backRoute: 'playbook.payments.needs-review',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.payments.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
        {
          component: () => import('@/pages/playbook/shared/needs-review.vue'),
          meta: { icon: 'fa-microscope', label: 'Needs Review', collection: 'payments' },
          name: 'playbook.payments.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/shared/proposal.vue'),
          meta: { label: 'Proposal' },
          name: 'playbook.payments.proposal',
          path: 'proposal/:proposalId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/KnowledgeLayout.vue'),
      meta: {
        icon: 'fa-sack-dollar',
        label: 'Payments',
      },
      name: 'playbook.payments',
      path: 'payments',
      props: { domain: 'payments' },
      redirect: {
        name: 'playbook.payments.needs-review',
      },
    },
    {
      component: () => import('@/pages/playbook/home.vue'),
      meta: {
        icon: 'fa-home',
        label: 'Home',
      },
      name: 'playbook.home',
      path: 'home',
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/needs-review.vue'),
          meta: {
            icon: 'fa-microscope',
            label: 'Needs Review',
          },
          name: 'playbook.needs-review',
          path: 'needs-review',
        },
        {
          component: () => import('@/pages/playbook/shared/NewArticleTemplates.vue'),
          meta: { label: 'New Article', icon: 'fa-brain' },
          name: 'playbook.knowledge.new',
          path: 'knowledge/new',
          props: {
            articleRoute: 'playbook.knowledge.article',
            backRoute: 'playbook.needs-review',
          },
        },
        {
          component: () => import('@/components/playbook/PlaybookArticleDetail.vue'),
          meta: { label: 'Article', icon: 'fa-brain' },
          name: 'playbook.knowledge.article',
          path: 'knowledge/:articleId',
          props: true,
        },
      ],
      component: () => import('@/pages/playbook/shared/GlobalKnowledgeLayout.vue'),
      meta: {
        icon: 'fa-microscope',
        label: 'Needs Review',
      },
      name: 'playbook.global',
      path: '',
    },
    {
      children: [
        {
          children: [
            {
              component: () => import('@/pages/playbook/templates/email/:id.vue'),
              meta: { label: 'Template' },
              name: 'playbook.templates.email.id',
              path: ':id',
            },
          ],
          component: () => import('@/pages/playbook/templates/email.vue'),
          meta: { icon: 'fa-diagram-cells', label: 'Email Templates' },
          name: 'playbook.templates.email',
          path: 'email',
        },
        {
          children: [
            {
              component: () => import('@/pages/playbook/templates/sms/:id.vue'),
              meta: { label: 'SMS Template' },
              name: 'playbook.templates.sms.id',
              path: ':id',
            },
          ],
          component: () => import('@/pages/playbook/templates/sms.vue'),
          meta: { icon: 'fa-message-sms', label: 'SMS Templates' },
          name: 'playbook.templates.sms',
          path: 'sms',
        },
        {
          component: () => import('@/pages/playbook/templates/custom-tables.vue'),
          meta: { icon: 'fa-table', label: 'Custom Tables' },
          name: 'playbook.templates.custom-tables',
          path: 'custom-tables',
        },
      ],
      component: () => import('@/pages/playbook/templates.vue'),
      meta: {
        description: 'Manage your email and SMS templates.',
        icon: 'fa-books',
        label: 'Templates',
      },
      name: 'playbook.templates',
      path: 'templates',
      redirect: {
        name: 'playbook.templates.email',
      },
    },
    {
      children: [
        {
          component: () => import('@/pages/playbook/build-status/pipeline.vue'),
          meta: { icon: 'fa-arrow-progress', label: 'Pipeline' },
          name: 'playbook.build-status.pipeline',
          path: 'pipeline',
        },
        {
          component: () => import('@/pages/playbook/build-status/source-data.vue'),
          meta: { icon: 'fa-database', label: 'Source Data' },
          name: 'playbook.build-status.source-data',
          path: 'source-data',
        },
      ],
      component: () => import('@/pages/playbook/build-status.vue'),
      meta: { icon: 'fa-circle-dot', label: 'Build Status' },
      name: 'playbook.build-status',
      path: 'build-status',
      redirect: { name: 'playbook.build-status.pipeline' },
    },
  ],
  component: () => import('@/pages/playbook/layout.vue'),
  meta: {
    description: 'Manage agent knowledge',
    icon: 'fa-book-open',
    label: 'Playbook',
  },
  name: 'playbook',
  path: 'playbook',
  redirect: {
    name: 'playbook.home',
  },
};
